/**
  * @class msysWM
  * @description
  * Window Manager system service manages display screen real estate.
  * Whenever there is a change, notifies listeners with affected window size through mmWindow message.
  * msysWM is a special service used for managing logical windows of GUI applications.
  * It must be included in every GUI application.
  * All modules (i.e. GUI listeners) depends on msysWM.
  * Functions of msysWM are very broad:
  * - handles HTML document of browser windows
  * - listens to mmWindowSet requests from GUI modules and recalculates window space
  *   appropriately, informing all modules whose window space has changed using mmWindow message
  * - handles window size changes, recalculates window space for all modules and
  *   informs all modules whose window space has changed using mmWindow message
  * - listens to mmWindowFocus message to bring requesting full-screen module to the front
  *   recalculates window space for all modules and informs all modules whose window space
  *   has changed using mmWindow message and also informs queue #WM by
  *   message mmWindowFocused on primary module change
  * - informs all applications on changed language, behaviour and theme using
  *   mmSetLanguage, mmSetBehavior and mmSetTheme messages
  * - handles keyboard events and publishes them to #KEYBOARD queue
  * - performs translation of styling variables
  */

import msysListener from 'msysListener'
import { Device } from '@capacitor/device'
import { App } from '@capacitor/app'
import { StatusBar } from '@capacitor/status-bar'
import { getSunrise, getSunset } from 'sunrise-sunset-js'
import geo from 'msysGeo'
let nw
const mfs = require('msysFS')

const NOTCHES = ['iPhone10,3', 'iPhone10,6', 'iPhone11,2', 'iPhone11,4', 'iPhone11,6', 'iPhone11,8', 'iPhone12,1', 'iPhone12,3', 'iPhone12,5']

export default class msysWM extends msysListener {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([], dependencies)
    cfg_schema = _.extend({
      animate_steps: { type: 'number', default: 10, description: 'number of steps for window animations' },
      animate_time: { type: 'number', default: 50, description: 'duration of window animations in miliseconds' },
      lang: { type: 'string', description: 'startup language in 2 character ISO 639-1', pattern: '^\\w\\w$', default: 'en' },
      styleset: { type: 'string', description: 'which configuration should apply', default: 'default' },
      style_variables: { type: 'object', description: 'styling variables' },
      behavior: { type: 'string', description: 'startup behavior', enum: ['desktop', 'dashboard', 'tablet', 'phone'], default: 'dashboard' },
      theme: { type: 'string', description: 'startup color theme', enum: ['day', 'night', 'auto'], default: 'auto' },
      fullscreen: { type: 'boolean', description: 'whether to start in fullscreen mode', default: true },
      alwaysontop: { type: 'boolean', description: 'whether to keep in always on top of other windows', default: true },
      start: { type: 'string', description: 'initial app module', default: 'mmodMap' },
      move_x: { type: 'integer', description: 'offset of left side of windows' },
      move_y: { type: 'integer', description: 'offset of top side of windows' },
      crop_w: { type: 'number', description: 'crop width' },
      crop_h: { type: 'number', description: 'crop height' },
      location: { type: 'string', description: 'geohash location, used for day/night auto mode', default: _.CONSTANTS.Soitron },
      mode: { type: 'string', description: 'startup app mode', enum: ['user', 'admin'], default: 'user' },
      set_url_hash: { type: 'boolean', description: 'whether to update URL hash', default: true },
      default_admin_module: { type: 'string', description: 'module to be selected when switched to admin mode', default: 'mmodStatus' },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
    this.last_focused = {}
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Received automatically (through inheritance of msysListener) when configuration and all dependencies were fulfilled.
  * Note that dependancy is fulfilled also when it exited instead of started.
  * @param {object} sender - Sender of the message
  * @param {object} params - not used
  */
  async mmStart(sender, params) {
    this.language = this.config.lang
    this.behavior = this.config.behavior
    this.theme = this.config.theme
    this.mode = this.config.mode

    // adjust from URL hash
    const theme = /[#&]theme=(\w+)/.exec(location.hash)?.[1]
    if(theme === 'day' || theme === 'night') {
      this.theme = theme
    }

    const lang = /[#&]lang=(\w+)/.exec(location.hash)?.[1]
    if(lang) {
      this.language = lang
    }

    this.screens = []
    this.windows = {}

    const me=this
    function _refreshScreens() { me._refreshScreens() }

    // enable/disable fullscreen on NWJS
    const win = global.nw ? global.nw.Window.get() : null
    if(win) {
      if(this.config.fullscreen) {
        win.enterKioskMode()
        if(this.config.alwaysontop) {
          win.setAlwaysOnTop(true)
        }else {
          win.setAlwaysOnTop(false)
        }
      }else{
        win.leaveKioskMode()
        win.setAlwaysOnTop(false)
        if(!isNaN(this.config.move_x) && !isNaN(this.config.move_y)) win.moveTo(this.config.move_x, this.config.move_y)
      }
    }

    if(nw) {
      nw.Screen.Init()
      nw.Screen.on('displayBoundsChanged', _refreshScreens)
      nw.Screen.on('displayAdded', _refreshScreens)
      nw.Screen.on('displayRemoved', _refreshScreens)
    }else if(typeof (window)==='object') {
      window.onresize = _refreshScreens
      window.onorientationchange = ()=>{
        setTimeout(_refreshScreens, 500)
      }
      let last_change = ''
      if(this.config.set_url_hash) {
        window.updateUrlHash=(hash)=>{
          last_change = hash
          window.location.hash = hash
        }
        window.setUrlHash=(hash)=>{
          last_change = ''
          window.location.hash = hash
        }
      }
      window.onhashchange=()=>{
        const hash = window.location.hash
        if(String(hash??'')===String(last_change??'')) {
          // ignore doublehash, meaning change hash but don't perform action
          last_change = ''
          return
        }
        const link = hash.match(/^#(mmod\w+)(?:\/(.*))?/)
        if(link) {
          this.publish('#ME', 'mmWindowFocus', { id: link[1] })
          if(link[2]) this.publish(link[1], 'mmUrlHash', { hash: link[2] })
        }

        const theme = /[#&]theme=(\w+)/.exec(location.hash)?.[1] === 'night' ? 'night' : 'day'
        if(this.theme !== theme) {
          this.publish('#ALLWITHME', 'mmSetTheme', { theme })
        }

        const lang = /[#&]lang=(\w+)/.exec(location.hash)?.[1]
        if(lang && lang !== this.language) {
          this.publish('#ALLWITHME', 'mmSetLanguage', { lang })
        }

        const appbar = /#appbar=(\w+)/.exec(location.hash)?.[1]
        if(appbar !== undefined && appbar !== null) {
          this._recalculateWindows()
        }
      }
    }else{
      super.mmExit(sender, { error: 'not in browser' })
      return
    }
    _refreshScreens()

    this.mmSetTheme(sender, { theme: this.theme })
    this.mmMode(sender, { mode: this.mode })

    App.addListener('backButton', (e)=>{
      this.publish('#KEYPRESS', 'mmKeypress', {
        key: 'back',
        keyCode: 99,
        shift: false,
        ctrl: false,
        alt: false,
        meta: false,
      })
    })

    document.addEventListener('keydown', this.keydownHandler.bind(this), false)
    document.body.addEventListener('mousemove', this.interactionHandler)
    document.body.addEventListener('scroll', this.interactionHandler)
    document.body.addEventListener('keydown', this.interactionHandler)
    document.body.addEventListener('click', this.interactionHandler)
    document.body.addEventListener('touchstart', this.interactionHandler)

    super.mmStart(sender, params)
  } // mmStart

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Automatically invoked when all expected listeners were started or exited
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {integer} [params.init] - timestamp when application starts with loading listeners
  * @param {integer} [params.loaded] - timestamp when all listeners are loaded and first listener starts
  * @param {integer} [params.started] - timestamp when all listeners started
  */
  mmStartedAll(sender, params) {
    this.publish('#ALLWITHME', 'mmSetLanguage', { lang: this.language })
    this.publish('#ALLWITHME', 'mmSetBehavior', { behavior: this.config.behavior })
    this.publish('#ALLWITHME', 'mmSetTheme', { theme: this.theme })
    this.publish('#ALLWITHME', 'mmMode', { mode: this.mode })
    if(this.config.start) { this.publish('#ME', 'mmWindowFocus', { id: this.config.start }) }
    this.started_all = true
  } // mmStartedAll

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Listener uses this message to notify #ALL that it successfully finished starting. Normally is not invoked manually, but through this.started() method.
   * @param {object} sender - message sender
   * @param {object} params - message parameters
   * @param {integer} [params.duration]
   */
  mmStarted(sender, params) {
    if(!this.started_all) return
    if(sender.listener_id === this.listener_id) return
    this.publish(sender, 'mmSetLanguage', { lang: this.language })
    this.publish(sender, 'mmSetBehavior', { behavior: this.config.behavior })
    this.publish(sender, 'mmSetTheme', { theme: this.theme })
    this.publish(sender, 'mmMode', { mode: this.mode })
  } // mmStarted

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to Window Manager to change definition of the window
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id] - ID of window (listener) to change; if not specified, sender is used
  * @param {string} [params.type] - type of window
  * @param {integer} [params.screen] - index of screen where the window wants to be placed; if it is higher than number of screens, last available screen is used
  * @param {string} [params.x] - requested position of left side of the window; if ends with %, it is calculated as screen width percentage; if negative, calculates from right of the screen
  * @param {string} [params.y] - requested position of top side of the window; if ends with %, it is calculated as screen height percentage; if negative, calculates from bottom of screen
  * @param {string} [params.width] - requested width of window; if ends with %, it is calculated as screen width percentage; if negative, calculates from right of the screen
  * @param {string} [params.height] - requested height of window; if ends with %, it is calculated as screen height percentage; if negative, calculates from bottom of screen
  * @param {string} [params.side] - side where the docked/widget window is attached
  * @param {integer} [params.order] - used for ordering docked or fullscreen windows; 0 is top level, smaller are below it
  * @param {boolean} [params.animate] - whether to animate repositioning/resizing
  */
  mmWindowSet(sender, params) {
    const id = String(params.id??'') || String(sender.listener_id??'')
    const win = this.windows[id] = this.windows[id] || { id, def: {}, area: {} }
    win.def = params

    const last_screen_idx = this.screens.length-1
    let screen_idx = Math.floor(this.windows[id].def.screen)
    screen_idx = screen_idx>last_screen_idx ? last_screen_idx : screen_idx>0 ? screen_idx : 0
    win.def.screen = screen_idx
    const screen = this.screens[screen_idx]

    // custom habndling to avoid full recalculation of windows if not needed
    if(screen && params.type==='full' && screen.windows.full && screen.windows.full.length>0) {
      const last_on_top = String(screen.windows.full[0]??'')
      screen.windows.full = screen.windows.full.filter(w=>String(w.id??'')!==id).concat(win).sort((a, b)=>a.def.order-b.def.order || a.id-b.id)
      const new_on_top = String(screen.windows.full[0]??'')
      if(new_on_top !== id && last_on_top !== id) {
        // special handling if on-top full window did not change
        if(!win.area || win.area.width!==0 || win.area.height!==0) {
          win.area = { x: 0, y: 0, width: 0, height: 0, z: 0 }
          const p = _.extend(win.area, { doc: screen.doc, orientation: screen.orientation })
          if(String(this.focused_window??'')===id) p.focused = true
          this.publish(win.id, 'mmWindow', p)
        }
        return
      }
    }

    this._recalculateWindows()
  } // mmWindowSet

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * refreshes list of available screens either from NWjs.Screens or from browser window
  */
  _refreshScreens() {
    const os = mfs.os()
    const platform = mfs.platform()

    document.body.setAttribute('lang', this.language || 'en')
    document.body.setAttribute('apptype', MOSY_APP)
    document.body.setAttribute('customer', MOSY_CUSTOMER)
    if(MOSY_PRODUCTION) document.body.setAttribute('production', MOSY_APP)

    if(nw) {
      const nw_screens = nw.Screen.screens
      const screens = []
      for(let i=0; i<nw_screens.length; i++) {
        const w = nw_screens[i].work_area
        screens.push({
          id: nw_screens[i].id,
          order: i,
          area: { x: w.x, y: w.y, width: w.width, height: w.height },
          orientation: w.width>=w.height ? 'landscape' : 'portrait',
          doc: document, // TODO: REPLACE WITH REAL document.body from NWjs
          window,
        })
      }
      this.screens = screens
    }else {
      let header_offset = (os==='ios' && (window.innerWidth < window.innerHeight)) && platform==='capacitor' ? 20 : 0
      let isNotch = false
      if(os === 'ios' && platform==='capacitor') {
        const model = Device.getInfo()
        isNotch = NOTCHES.includes(model)
        header_offset = header_offset * 1.75
        document.body.classList.add(isNotch ? 'iphone-notch' : 'iphone')
      }
      this.screens = [{
        id: 0,
        order: 0,
        area: {
          x: 0,
          y: header_offset,
          width: window.innerWidth,
          height: window.innerHeight-header_offset-(isNotch?20:0),
        },
        orientation: window.innerWidth>=window.innerHeight-header_offset ? 'landscape' : 'portrait',
        doc: document,
        window,
      }]
    }

    this.screens.forEach(screen=>{
      const crop_w = _.natural(this.config.crop_w)
      if(crop_w>0) {
        screen.area.width = _.min([screen.area.width, crop_w])
        document.body.style.width = crop_w + 'px'
      }
      const crop_h = _.natural(this.config.crop_h)
      if(crop_h>0) {
        screen.area.height = _.min([screen.area.height, crop_h])
        document.body.style.height = crop_h + 'px'
      }
    })

    this.screens.forEach((screen)=>{
      screen.changeStyleVariables = ()=>{ this.changeStyleVariables(screen.window) }
    })

    this._recalculateWindows()

    this._recalculateModals()

    this.publish('#ALLWITHME', 'mmSetBehavior', { behavior: this.behavior })
  } // _refreshScreens

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to set language
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.lang] - desired language in 2 character ISO 639-1
  */
  mmSetLanguage(sender, params) {
    this.language = params.lang
    document.body.setAttribute('lang', this.language)
  } // mmSetLanguage

  _changeBehavior(behavior) {
    if(document?.body) { document.body.setAttribute('behavior', _.toString(behavior)) }
  }

  _changeTheme(theme) {
    if(document?.body) { document.body.setAttribute('theme', _.toString(theme)) }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * recalculates position/size of all windows and notify those changed
  */
  _recalculateWindows() {
    // clear screens' windows
    const last_screen_idx = this.screens.length - 1
    this.screens.forEach(screen => {
      screen.windows = { dock: [], full: [], widget: [], float: [], none: [] }
    })

    // put each window into appropriate list of windows for its screen
    for (const id in this.windows) {
      let win = this.windows[id]
      if(id === 'mmodAppBar') {
        if(/[#&]appbar=false/.test(location.hash)) {
          win = { id: win.id, def: { type: 'none' }, area: {} }
        }
      }
      let screen_idx = Math.floor(win.def.screen)
      screen_idx = screen_idx > last_screen_idx ? last_screen_idx : screen_idx > 0 ? screen_idx : 0
      win.def.screen = screen_idx
      const screen = this.screens[screen_idx]
      const type = win.def.type
      if (!screen.windows[type]) screen.windows[type] = []
      screen.windows[type].push(win)
    }

    // sort each window type in each screen
    this.screens.forEach(screen => {
      for (const type in screen.windows) {
        screen.windows[type] = screen.windows[type].sort((a, b) => a.def.order - b.def.order || a.id - b.id)
      }
    })

    // this contains list of new areas for windows
    const changes = {}

    this.screens.forEach(screen => {
      const screen_width = screen.area.width
      const screen_height = screen.area.height

      // docked windows and remaining area for other windows
      let available_area = screen.available_area = { x: 0, y: 0, width: screen.area.width, height: screen.area.height }
      screen.windows.dock.forEach(win => {
        const side = win.def.side
        const win_area = changes[win.id] = { z: 1000 }
        if (side === 'left') {
          win_area.x = available_area.x
          win_area.y = available_area.y
          win_area.width = _.min([size2pixels(win.def.width, screen_width), available_area.width])
          win_area.height = available_area.height
          available_area.x += win_area.width
          available_area.width -= win_area.width
        } else if (side === 'right') {
          win_area.y = available_area.y
          win_area.width = _.min([size2pixels(win.def.width, screen_width), available_area.width])
          win_area.x = available_area.x + available_area.width - win_area.width
          win_area.height = available_area.height
          available_area.width -= win_area.width
        } else if (side === 'top') {
          win_area.x = available_area.x
          win_area.y = available_area.y
          win_area.width = available_area.width
          win_area.height = _.min([size2pixels(win.def.height, screen_height), available_area.height])
          available_area.y += win_area.height
          available_area.height -= win_area.height
        } else if (side === 'bottom') {
          win_area.x = available_area.x
          win_area.width = available_area.width
          win_area.height = _.min([size2pixels(win.def.height, screen_height), available_area.height])
          win_area.y = available_area.y + available_area.height - win_area.height
          available_area.height -= win_area.height
        }
      })

      // full screen windows; one on top gets screen space remaining after docked windows, others gets none
      screen.windows.full.forEach((win, idx) => {
        if (idx === 0) {
          // first full window gets all screen area remaining after docked windows
          changes[win.id] = JSON.parse(JSON.stringify(available_area))
          changes[win.id].z = 0
        } else {
          // all other full windows gets no space
          changes[win.id] = { x: 0, y: 0, width: 0, height: 0, z: 0 }
        }
      })

      // floating windows; each have separate layer, so they are not affected nor affecting other windows
      available_area = { x: 0, y: 0, width: screen.area.width, height: screen.area.height }
      screen.windows.float.forEach(win => {
        const x = _.min([size2pixels(win.def.x, screen_width), screen_width])
        const y = _.min([size2pixels(win.def.y, screen_height), screen_height])
        changes[win.id] = {
          x,
          y,
          width: _.min([size2pixels(win.def.width, screen_width), screen_width - x]),
          height: _.min([size2pixels(win.def.height, screen_height), screen_height - y]),
          z: 10000 - parseInt(win.def.order || 0),
        }
      })

      // undisplayed windows
      screen.windows.none.forEach(win => {
        changes[win.id] = { x: 0, y: 0, width: 0, height: 0, z: 0 }
      })
    })

    let resizes = 0
    for(let id in changes) {
      id = String(id??'')
      const screen = this.screens[this.windows[id].def.screen]
      changes[id].x += screen.area.x
      changes[id].y += screen.area.y
      const win = this.windows[id]
      if (!_.isEqual(win.area, changes[id]) || String(this.focused_window??'') === id) {
        this._resizeWindow(id, changes[id])
        resizes++
      }
    }
    this.debug('Resized', resizes, 'windows')
  } // _recalculateWindows

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * updates given window with new position/size
  * if it is changed, notifies affected listener
  * send several continuous updates if animation was requested
  */
  _resizeWindow(id, new_area) {
    id = String(id??'')
    const win = this.windows[id]
    const screen = this.screens[win.def.screen]
    if (!win.area || Object.keys(win.area).length === 0) win.area = { x: 0, y: 0, width: 0, height: 0, z: new_area.z }
    if (win.def && win.def.animate) {
      const step_duration = parseInt(this.config.animate_time / this.config.animate_steps)
      for (let i = 0; i < this.config.animate_steps - 1; i++) {
        const temp_area = _.cloneDeep(win.area)
        temp_area.x += parseInt(i * (new_area.x - win.area.x) / this.config.animate_steps)
        temp_area.y += parseInt(i * (new_area.y - win.area.y) / this.config.animate_steps)
        temp_area.width += parseInt(i * (new_area.width - win.area.width) / this.config.animate_steps)
        temp_area.height += parseInt(i * (new_area.height - win.area.height) / this.config.animate_steps)
        setTimeout((area) => {
          const p = _.extend(area, { doc: screen.doc, orientation: screen.orientation })
          if(String(this.focused_window??'') === id) p.focused = true
          this.publish(win.id, 'mmWindow', p)
        }, i * step_duration, temp_area)
      }
      setTimeout(() => {
        win.area = _.cloneDeep(new_area)
        const p = _.extend(new_area, { doc: screen.doc, orientation: screen.orientation })
        if(String(this.focused_window??'') === id) p.focused = true
        this.publish(win.id, 'mmWindow', p)
      }, this.config.animate_time)
    } else {
      win.area = _.cloneDeep(new_area)
      const p = _.extend(new_area, { doc: screen.doc, orientation: screen.orientation })
      if(String(this.focused_window??'') === id) p.focused = true
      this.publish(win.id, 'mmWindow', p)
    }
  } // _resizeWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * recalculates modal window(s)
  */
  _recalculateModals() {
    this.screens.forEach(screen=>{
      screen.doc.body.forAll('>.mele-modal,>.mele-modalwindow', el=>{
        try{
          el.reposition()
        }catch(e) {
          // ignore
        }
      })
    })
  } // _recalculateModals

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * change behavior attribute of modal windows
  */
  _modalsSetBehavior(behavior) {
    this.screens.forEach(screen=>{
      screen.doc.body.forAll('>.mele-modal,>.mele-modalwindow', el=>{
        try{
          el.setAttribute('behavior', behavior)
        }catch(e) {
          // ignore
        }
      })
    })
  } // _modalsSetBehavior

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * change theme attribute of modal windows
  */
  _modalsSetTheme(theme) {
    this.screens.forEach(screen=>{
      screen.doc.body.forAll('>.mele-modal,>.mele-modalwindow', el=>{
        try{
          el.setAttribute('theme', theme)
        }catch(e) {
          // ignore
        }
      })
    })
  } // _modalsSetTheme

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to WM to bring given window (or sender's if `id` not specified) on top. Meaningful for full screen windows only.
  * @param {object} sender - Sender of the message
  * @param {object} params - message parameters
  * @param {string} [params.id]
  */
  mmWindowFocus(sender, params) {
    const id = String(params.id || sender.listener_id || '')
    const win = this.windows[id]
    if (!_.isObjectLike(win) || win.def.type !== 'full') return
    const screen = parseInt(win.def.screen) || 0

    // Get the current focused window before changing focus
    const currentFocusedWindowId = this.focused_window

    // Unfocus all other windows
    for (const otherId in this.windows) {
      const otherWin = this.windows[otherId]
      if (otherId !== id && otherWin.focused) {
        // Mark window as unfocused and send the unfocused message
        otherWin.focused = false
        this.publish(otherId, 'mmWindowUnfocused', { id: otherId })
      }
    }

    // Mark this window as focused
    this.windows[id].focused = true
    this.focused_window = id

    // Reorder the full-screen windows on the same screen and bring the new window to the front
    const screen_windows = _.sortBy(
      _.values(this.windows).filter((win) => String(win.id ?? '') !== id && win.def && (parseInt(win.def.screen) || 0) === screen && win.def.type === 'full'),
      ['def.order', 'id'],
    )
    screen_windows.unshift(win)
    for (let i = 0; i < screen_windows.length; i++) {
      screen_windows[i].def.order = i
    }

    // Recalculate windows to apply changes
    this._recalculateWindows()

    // Update the URL hash if applicable
    if (window && window.updateUrlHash) {
      const m = /^#(\w+)/.exec(window.location.hash)
      if (!m || m[1] !== id) window.updateUrlHash('#' + id)
    }

    // Store the last focused window for the current mode
    this.last_focused[this.mode] = id

    // Notify the focused window and the window manager
    this.publish(id, 'mmWindowFocused', { id })
    this.publish('#WM', 'mmWindowFocused', { id })
  } // mmWindowFocus

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to change application color theme
  * @param {object} sender - Sender of the message
  * @param {object} params - message parameters
  * @param {string} [params.theme] - desired color theme
  */
  mmSetTheme(sender, params) {
    if (params.theme === 'auto') {
      this.selectAutoTheme()
    } else {
      this.theme = params.theme
    }

    document.body.setAttribute('theme', this.theme)
    document.body.setAttribute('styleset', this.config.styleset)
  } // mmSetTheme

  selectAutoTheme() {
    // caluclate automatic day/night mode
    const current_time = new Date()
    const [lat, lng] = geo.toLatLng(this.config.location)
    const sunrise = getSunrise(lat, lng)
    const sunset = getSunset(lat, lng)
    this.debug('SUNRISE AT:', sunrise, 'SUNSET AT:', sunset, 'CURRENT TIME', current_time)
    this.theme = current_time.valueOf() >= sunrise.valueOf() && current_time.valueOf() < sunset.valueOf() ? 'day' : 'night'
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to change application behavior
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.behavior] - desired behavior
  */
  mmSetBehavior(sender, params) {
    this.behavior = params.behavior || this.behavior || 'tablet'
    const device2 = mfs.device()
    const outcoming_behavior = device2==='phone' ? 'phone' : this.behavior
    this._modalsSetBehavior(outcoming_behavior)
    this._changeBehavior(outcoming_behavior)
    if(outcoming_behavior === 'phone' && !this.statusbar_daemon) {
      // this.statusbar_daemon = this.startDaemon(5000, this.hideStatusBar.bind(this))
    }
  } // mmSetBehavior

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * request to msysWM to change window URL hash and optionally perform appropriate action
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.hash - part of URL hash parametrizing the request
  * @param {string} [params.noaction] - if set, no associated action will be taken
  */
  mmSetUrlHash(sender, params) {
    if(params.noaction) {
      if(window && window.updateUrlHash) window.updateUrlHash(params.hash)
    }else{
      if(window && window.setUrlHash) {
        window.setUrlHash(params.hash)
      }else{
        const link = params.hash.match(/^#(mmod\w+)(?:\/(.*))?/)
        if(link) {
          this.publish('#ME', 'mmWindowFocus', { id: link[1] })
          if(link[2]) this.publish(link[1], 'mmUrlHash', { hash: link[2] })
        }
      }
    }
  } // mmSetUrlHash

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * change of application mode
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.mode - mode to be set
  */
  mmMode(sender, params) {
    this.mode = params.mode
    if(document?.body) { document.body.setAttribute('mode', _.toString(this.mode)) }
    let last = this.last_focused[this.mode]
    if(!last && this.mode==='admin') last = this.config.default_admin_module
    this.publish('#ME', 'mmWindowFocus', { id: last })
  } // mmMode

  keydownHandler(e) {
    let { key, keyCode, charCode, repeat, shiftKey, ctrlKey, altKey, metaKey } = e
    if(!keyCode && charCode) keyCode = charCode // backwards compatibility
    if(!key || !keyCode) return
    const out = { key, keyCode }
    if(repeat) out.repeat = true
    if(shiftKey) out.shift = true
    if(ctrlKey) out.ctrl = true
    if(altKey) out.alt = true
    if(metaKey) out.meta = true
    if(key === 'ArrowDown' || key === 'ArrowUp' || key === 'ArrowLeft' || key === 'ArrowRight') {
      const el = document.activeElement
      if(!(el && !el.disabled && !el.readOnly && (el.matches('input, textarea, select') || el.isContentEditable))) {
        e.preventDefault()
        this.publish('#KEYPRESS', 'mmKeypress', out)
      }
    }else {
      this.publish('#KEYPRESS', 'mmKeypress', out)
    }
  } // keydownHandler

  interactionHandler(e) {
    e.srcElement.ownerDocument.userInteraction = true
  } // interactionHandler

  async hideStatusBar() {
    try{
      const info = await StatusBar.getInfo()
      if(info.visible) StatusBar.hide()
    }catch(e) {
      // ignore
    }
  }
} // msysWM

// helper function to calculate real pixels from percentage
function size2pixels(val, full_size) {
  const str = _.toString(val)
  val = parseInt(str.substr(str.length-1)==='%' ? parseFloat(str.substr(0, str.length-1))*full_size/100 : str) || 0
  if(str.substr(0, 1)==='-') val = full_size + val
  return val
}
